import {
  Box,
  Grid,
  Input,
  Select,
  Checkbox,
  Button,
  Text,
  Flex,
  FormLabel,
  FormControl,
  FormErrorMessage,
  useToast,
  IconButton,
  HStack,
} from "@chakra-ui/react";
import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "../../configs/axios.config";
import { FiTrash2, FiPlus } from "react-icons/fi";
import BreadCrumbMaker from "../../components/BreadCrumbMaker";

const NAME_REGEX = /^[A-Za-z][A-Za-z\s'.-]{1,49}$/;
const CITY_REGEX = /^[A-Za-z][A-Za-z\s'.-]{1,49}$/;
const LANGUAGE_REGEX = /^[A-Za-z\s,/-]{2,60}$/;
const INDIAN_MOBILE_REGEX = /^[6-9]\d{9}$/;
const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/i;

const sanitizePhoneInput = (value) =>
  value.replace(/\D/g, "").slice(0, 10);

const sanitizeTextInput = (value, maxLength = 50) =>
  value.replace(/\s+/g, " ").slice(0, maxLength);

const validatePersonName = (value, label = "Name") => {
  const trimmed = value.trim();
  if (!trimmed) return `${label} is required`;
  if (!NAME_REGEX.test(trimmed)) {
    return `${label} must be 2-50 characters and contain only letters, spaces, apostrophes, dots, or hyphens`;
  }
  return "";
};

const validatePhoneNumber = (value, label) => {
  const cleaned = value.trim();
  if (!cleaned) return `${label} is required`;
  if (!INDIAN_MOBILE_REGEX.test(cleaned)) {
    return `${label} must be a valid 10-digit Indian mobile number`;
  }
  return "";
};

const validateOptionalEmail = (value) => {
  const trimmed = value.trim();
  if (!trimmed) return "";
  if (!EMAIL_REGEX.test(trimmed)) {
    return "Enter a valid email address";
  }
  if (trimmed.length > 100) {
    return "Email must be 100 characters or fewer";
  }
  return "";
};

const validateCity = (value) => {
  const trimmed = value.trim();
  if (!trimmed) return "City is required";
  if (!CITY_REGEX.test(trimmed)) {
    return "City must be 2-50 characters and contain only letters, spaces, apostrophes, dots, or hyphens";
  }
  return "";
};

const validateLanguage = (value) => {
  const trimmed = value.trim();
  if (!trimmed) return "";
  if (!LANGUAGE_REGEX.test(trimmed)) {
    return "Language can only contain letters, spaces, commas, slashes, or hyphens";
  }
  return "";
};

const Booking = () => {
  const navigate = useNavigate();
  const toast = useToast();

  // ---------------------------
  // Initialize form
  // ---------------------------
  const [form, setForm] = useState({
    name: "",
    email: "",
    mobile: "",
    whatsapp: "",
    city: "",
    gender: "",
    language: "",
    comments: "",
  });

  const [passengers, setPassengers] = useState([
    { name: "", mobile: "", whatsapp: "" },
  ]);

  const [errors, setErrors] = useState({});
  const [passengerErrors, setPassengerErrors] = useState([{}]);
  const [leadFetchAttempted, setLeadFetchAttempted] = useState(false);
  const [whatsappSameAsMobile, setWhatsappSameAsMobile] = useState(true);
  const [bookingType, setBookingType] = useState("LOCAL");
  // LOCAL | OUTSTATION
  const [bookingData, setBookingData] = useState(null);

  const [passengerWhatsappSameAsMobile, setPassengerWhatsappSameAsMobile] =
    useState([true]);

  // Monitor form changes for debugging
  useEffect(() => {
    console.log("📝 Form state changed:", form);
  }, [form]);

  // for copying customer to passenger by default
  useEffect(() => {
    if (!passengers.length) return;
    const passenger = passengers[0];
    // Only auto-sync if passenger is still auto-managed
    const shouldSync =
      passenger.name === "" &&
      passenger.mobile === "" &&
      passenger.whatsapp === "";
    if (!shouldSync) return;

    setPassengers((prev) => {
      const updated = [...prev];
      updated[0] = {
        ...updated[0],
        name: form.name,
        mobile: form.mobile,
        whatsapp: whatsappSameAsMobile ? form.mobile : form.whatsapp,
      };
      return updated;
    });
  }, [form.name, form.mobile, form.whatsapp]);

  // for syncing passenger mobile to whatsapp if checkbox is checked
  useEffect(() => {
    if (!whatsappSameAsMobile) return;

    setForm((prev) => {
      if (prev.whatsapp === prev.mobile) return prev;

      return {
        ...prev,
        whatsapp: prev.mobile,
      };
    });
  }, [form.mobile, whatsappSameAsMobile]);

  // ---------------------------
  // Prefill from localStorage if available
  // ---------------------------
  useEffect(() => {
    const storedBookingData = JSON.parse(
      localStorage.getItem("lead_booking_data"),
    );
    const customerData = JSON.parse(localStorage.getItem("customerDetails"));
    const selectedLeadId = localStorage.getItem("selectedLeadId");
    const selectedLeadData = localStorage.getItem("selectedLeadData");

    // console.log("=== Booking Details Page Load ===");
    // console.log("Selected Lead ID:", selectedLeadId);
    // console.log("Selected Lead Data exists:", !!selectedLeadData);
    // console.log("Booking Data exists:", !!storedBookingData);
    // console.log("Customer Data exists:", !!customerData);
    // console.log("================================");

    // If we have stored lead data, use it directly instead of fetching
    if (selectedLeadData) {
      try {
        const lead = JSON.parse(selectedLeadData);
        // console.log("Using stored lead data:", lead);
        processLeadData(lead);
      } catch (error) {
        console.error("Error parsing stored lead data:", error);
        // Fallback to fetching if stored data is corrupted
        if (
          selectedLeadId &&
          selectedLeadId !== "null" &&
          selectedLeadId !== "undefined"
        ) {
          setLeadFetchAttempted(true);
          fetchLeadData(selectedLeadId);
        }
      }
    } else if (
      selectedLeadId &&
      selectedLeadId !== "null" &&
      selectedLeadId !== "undefined"
    ) {
      // No stored data, try fetching from API
      setLeadFetchAttempted(true);
      fetchLeadData(selectedLeadId);
    } else if (customerData) {
      // console.log("Using customer data from localStorage");
      setForm((prev) => {
        const updated = {
          ...prev,
          ...customerData,
          passengerName: customerData.passengerName || "",
          passengerMobile: customerData.passengerMobile || "",
          passengerWhatsapp: customerData.passengerWhatsapp || "",
        };
        // console.log("Form updated from localStorage:", updated);
        return updated;
      });
    } else {
      // console.log("No lead or customer data found - starting with empty form");
      // Clear any stale lead ID
      localStorage.removeItem("selectedLeadId");
    }

    if (storedBookingData) {
      setBookingData(storedBookingData);
      // console.log("Prefilled Booking Data:", storedBookingData);
    }
  }, []);

  // Process lead data and populate form
  const processLeadData = (lead) => {
    // console.log("=== Processing Lead Data ===");
    // console.log("Lead object:", lead);
    // console.log("Customer ID object:", lead.customerID);
    // console.log("Customer object:", lead.customer);

    // Try to get customer data from different possible structures
    const customerObj = lead.customerID || lead.customer || lead;
    // console.log("Using customer object:", customerObj);

    // Helper function to strip +91 prefix if present
    const stripCountryCode = (number) => {
      if (!number) return "";
      const cleaned = number.trim().replace(/\s/g, "");
      return cleaned.startsWith("+91") ? cleaned.substring(3) : cleaned;
    };

    // Prefill form with lead customer data - handle multiple structures
    const customerData = {
      name: customerObj.name || lead.name || "",
      email: customerObj.email || lead.email || "",
      mobile: stripCountryCode(customerObj.mobile || lead.mobile || ""),
      whatsapp: stripCountryCode(
        customerObj.whatsapp ||
        lead.whatsapp ||
        customerObj.mobile ||
        lead.mobile ||
        "",
      ),
      city: customerObj.city || lead.city || "",
      gender: customerObj.gender || lead.gender || "",
      language: customerObj.language || lead.language || "",
      comments: customerObj.comments || lead.comments || lead.notes || "",
      passengerName:
        lead.passengers?.[0]?.name ||
        lead.passengerName ||
        customerObj.name ||
        lead.name ||
        "",
      passengerMobile: stripCountryCode(
        lead.passengers?.[0]?.mobile ||
        lead.passengerMobile ||
        customerObj.mobile ||
        lead.mobile ||
        "",
      ),
      passengerWhatsapp: stripCountryCode(
        lead.passengers?.[0]?.whatsapp ||
        lead.passengerWhatsapp ||
        customerObj.mobile ||
        lead.mobile ||
        "",
      ),
    };

    // console.log("Extracted customer data:", customerData);
    // console.log("Setting form with customer data...");
    // console.log("Current form state before update:", form);

    setForm((prev) => {
      const newForm = { ...prev, ...customerData };
      // console.log("New form state after update:", newForm);
      return newForm;
    });

    // Store customer ID for later use
    const customerId = customerObj._id || customerObj.id || lead._id;
    // console.log("Storing customer ID:", customerId);

    localStorage.setItem(
      "customerDetails",
      JSON.stringify({
        ...customerData,
        customerId: customerId,
      }),
    );

    // console.log("✓ Form updated successfully");
    // console.log("===========================");

    toast({
      title: "Lead Loaded",
      description: `Customer ${customerData.name || "details"} loaded successfully`,
      status: "success",
      duration: 2000,
      isClosable: true,
    });
  };

  // Fetch lead data to prefill form (fallback if stored data not available)
  const fetchLeadData = async (leadId) => {
    try {
      const token = localStorage.getItem("prima_session");
      if (!token) {
        console.log("No token found, skipping lead fetch");
        return;
      }

      // console.log("=== Fetching Lead Data ===");
      // console.log("Lead ID:", leadId);
      // console.log("Lead ID type:", typeof leadId);
      // console.log("Lead ID length:", leadId?.length);

      const apiUrl = `${import.meta.env.VITE_API_URL}/management/leads/${leadId}`;
      // console.log("Full API URL:", apiUrl);
      // console.log("API Base URL:", import.meta.env.VITE_API_URL);
      // console.log("========================");

      const res = await axios.get(apiUrl, {
        headers: { Authorization: `Bearer ${token}` },
      });

      // console.log("✓ Lead data fetched successfully");
      // console.log("Response data:", res.data);

      const lead = res.data?.data || res.data;

      if (!lead) {
        console.warn("No lead data in response");
        throw new Error("No lead data found");
      }

      // Use the common processLeadData function
      processLeadData(lead);
    } catch (error) {
      console.error("❌ Error fetching lead:", error);
      console.error("Error details:", {
        status: error.response?.status,
        statusText: error.response?.statusText,
        data: error.response?.data,
        url: error.config?.url,
        message: error.message,
      });

      // Clear the invalid lead ID
      localStorage.removeItem("selectedLeadId");

      // Only show toast if we actually tried to fetch a lead (not just stale data)
      if (leadFetchAttempted) {
        if (error.response?.status === 404) {
          console.log("Lead not found (404). Possible reasons:");
          console.log("1. Lead ID is invalid or lead was deleted");
          console.log("2. API endpoint path is incorrect");
          console.log("3. Lead exists but endpoint expects different format");

          // Only show toast if the error isn't "Not Found" message
          // This prevents showing error when the backend route doesn't exist yet
          if (!error.response?.data?.message?.includes("Not Found")) {
            toast({
              title: "Lead Not Available",
              description: "Continuing with new booking form.",
              status: "info",
              duration: 2000,
              isClosable: true,
            });
          }
        } else {
          toast({
            title: "Note",
            description: "Starting with a new booking form.",
            status: "info",
            duration: 2000,
            isClosable: true,
          });
        }
      }

      // Don't block the user - let them continue with empty form
      console.log("Continuing with empty form...");
    }
  };

  // ---------------------------
  // Handling Input
  // ---------------------------
  const handleChange = (e) => {
    const { name, value } = e.target;
    let nextValue = value;

    if (name === "mobile" || name === "whatsapp") {
      nextValue = sanitizePhoneInput(value);
    } else if (name === "name" || name === "city") {
      nextValue = sanitizeTextInput(value);
    } else if (name === "language") {
      nextValue = sanitizeTextInput(value, 60);
    } else if (name === "email") {
      nextValue = value.trimStart().slice(0, 100);
    } else if (name === "comments") {
      nextValue = value.slice(0, 250);
    }

    setForm((prev) => {
      const updated = { ...prev, [name]: nextValue };
      // If whatsapp checkbox is checked and mobile changes, update whatsapp too
      if (name === "mobile" && whatsappSameAsMobile) {
        updated.whatsapp = nextValue;
      }
      return updated;
    });

    setErrors((prev) => {
      if (!prev[name]) return prev;
      const updated = { ...prev };
      delete updated[name];
      return updated;
    });
  };

  // ---------------------------
  // Passenger Management
  // ---------------------------
  const handlePassengerChange = (index, field, value) => {
    const newPassengers = [...passengers];
    let nextValue = value;

    if (field === "mobile" || field === "whatsapp") {
      nextValue = sanitizePhoneInput(value);
    } else if (field === "name") {
      nextValue = sanitizeTextInput(value);
    }

    newPassengers[index][field] = nextValue;

    // If whatsapp checkbox is checked and mobile changes, update whatsapp too
    if (field === "mobile" && passengerWhatsappSameAsMobile[index]) {
      newPassengers[index].whatsapp = nextValue;
    }

    setPassengers(newPassengers);

    // Clear error for this field
    const newErrors = [...passengerErrors];
    if (newErrors[index]) {
      delete newErrors[index][field];
    }
    setPassengerErrors(newErrors);
  };

  const addPassenger = () => {
    setPassengers([...passengers, { name: "", mobile: "", whatsapp: "" }]);
    setPassengerErrors([...passengerErrors, {}]);
    setPassengerWhatsappSameAsMobile([...passengerWhatsappSameAsMobile, false]);
  };

  const removePassenger = (index) => {
    if (passengers.length === 1) {
      toast({
        title: "Cannot remove",
        description: "At least one passenger is required",
        status: "warning",
        duration: 2000,
        isClosable: true,
      });
      return;
    }
    const newPassengers = passengers.filter((_, i) => i !== index);
    const newErrors = passengerErrors.filter((_, i) => i !== index);
    const newCheckboxStates = passengerWhatsappSameAsMobile.filter(
      (_, i) => i !== index,
    );
    setPassengers(newPassengers);
    setPassengerErrors(newErrors);
    setPassengerWhatsappSameAsMobile(newCheckboxStates);
  };

  const copyCustomerToFirstPassenger = (checked) => {
    if (!passengers.length) return;

    const newPassengers = [...passengers];

    if (checked) {
      // sync from customer
      newPassengers[0] = {
        name: form.name,
        mobile: form.mobile,
        whatsapp: form.whatsapp,
      };
    } else {
      // UNSYNC: clear passenger so effect knows user took control
      newPassengers[0] = {
        name: "",
        mobile: "",
        whatsapp: "",
      };
    }
    setPassengers(newPassengers);
  };

  // ---------------------------
  // Validation
  // ---------------------------
  const validate = () => {
    let newErrors = {};

    const customerNameError = validatePersonName(form.name, "Name");
    if (customerNameError) newErrors.name = customerNameError;

    const emailError = validateOptionalEmail(form.email);
    if (emailError) newErrors.email = emailError;

    const mobileError = validatePhoneNumber(form.mobile, "Mobile number");
    if (mobileError) newErrors.mobile = mobileError;

    const whatsappError = validatePhoneNumber(form.whatsapp, "Whatsapp number");
    if (whatsappError) newErrors.whatsapp = whatsappError;

    const cityError = validateCity(form.city);
    if (cityError) newErrors.city = cityError;

    if (!form.gender.trim()) newErrors.gender = "Gender is required";

    const languageError = validateLanguage(form.language);
    if (languageError) newErrors.language = languageError;

    // Validate all passengers
    let newPassengerErrors = passengers.map((passenger) => {
      let passengerError = {};

      const passengerNameError = validatePersonName(
        passenger.name,
        "Passenger name",
      );
      if (passengerNameError) {
        passengerError.name = passengerNameError;
      }

      const passengerMobileError = validatePhoneNumber(
        passenger.mobile,
        "Mobile number",
      );
      if (passengerMobileError) {
        passengerError.mobile = passengerMobileError;
      }

      const passengerWhatsappError = validatePhoneNumber(
        passenger.whatsapp,
        "Whatsapp number",
      );
      if (passengerWhatsappError) {
        passengerError.whatsapp = passengerWhatsappError;
      }

      return passengerError;
    });

    setErrors(newErrors);
    setPassengerErrors(newPassengerErrors);

    const hasCustomerErrors = Object.keys(newErrors).length > 0;
    const hasPassengerErrors = newPassengerErrors.some(
      (err) => Object.keys(err).length > 0,
    );

    return !hasCustomerErrors && !hasPassengerErrors;
  };

  // ---------------------------
  // Submit Handler
  // ---------------------------
  const handleSubmit = async () => {
    if (!validate()) return;

    const token = localStorage.getItem("prima_session");
    if (!token) {
      toast({
        title: "Error",
        description: "User token not found. Login again.",
        status: "error",
        duration: 3000,
        isClosable: true,
      });
      return;
    }

    try {
      const selectedLeadId = localStorage.getItem("selectedLeadId");
      const existingCustomer = JSON.parse(
        localStorage.getItem("customerDetails"),
      );

      let customerId;

      // If lead was selected, use existing customer
      if (selectedLeadId && existingCustomer?.customerId) {
        customerId = existingCustomer.customerId;
        // console.log("Using existing customer from lead:", customerId);
      } else {
        // Create new customer
        // Helper function to ensure +91 prefix
        const ensureCountryCode = (number) => {
          if (!number) return "";
          const cleaned = number.trim().replace(/\s/g, "");
          return cleaned.startsWith("+91") ? cleaned : `+91${cleaned}`;
        };

        const payload = {
          name: form.name,
          email: form.email,
          mobile: ensureCountryCode(form.mobile),
          whatsapp: ensureCountryCode(form.whatsapp),
          city: form.city,
          gender: form.gender,
          language: form.language,
          comments: form.comments,
          isCorporate: false,
          type: "Individual",
          rating: 4,
          notes: "Booking lead",
          status: "Active",
        };

        // -----------------------------
        // Create customer
        // -----------------------------
        const res = await axios.post(
          `${import.meta.env.VITE_API_URL}/management/customer`,
          payload,
          {
            headers: {
              "Content-Type": "application/json",
              Authorization: `Bearer ${token}`,
            },
          },
        );

        // Correct Customer ID
        customerId = res?.data?.data?._id;

        if (!customerId) {
          console.error("Customer ID missing in response:", res.data);
          toast({
            title: "Error",
            description: "Customer ID not found in API response",
            status: "error",
            duration: 3000,
            isClosable: true,
          });
          return;
        }
      }

      // -----------------------------
      // Save customer & passenger
      // -----------------------------
      localStorage.setItem(
        "customerDetails",
        JSON.stringify({ ...form, customerId }),
      );

      // Helper function to ensure +91 prefix for passengers
      const ensureCountryCode = (number) => {
        if (!number) return "";
        const cleaned = number.trim().replace(/\s/g, "");
        return cleaned.startsWith("+91") ? cleaned : `+91${cleaned}`;
      };

      const passengersData = passengers.map((p) => ({
        name: p.name,
        mobile: ensureCountryCode(p.mobile),
        whatsapp: ensureCountryCode(p.whatsapp),
      }));
      localStorage.setItem("lead_passengers", JSON.stringify(passengersData));

      toast({
        title: "Success",
        description: "Customer & Passenger details saved successfully",
        status: "success",
        duration: 3000,
        isClosable: true,
      });

      // -----------------------------
      // Navigate to Booking Details page
      // -----------------------------
      navigate("/bookings/lead/booking-details", {
        state: { ...form, customerId },
      });
    } catch (error) {
      console.error("Error creating customer:", error);
      toast({
        title: "Something went wrong",
        description: error.response?.data?.message || "Internal Server Error",
        status: "error",
        duration: 3000,
        isClosable: true,
      });
    }
  };

  return (
    <Box p={6}>
      <Box mb={4}>
        <BreadCrumbMaker
          items={[
            { link: "/", title: "Dashboard" },
            { link: "/bookings", title: "Bookings" },
            { link: "/bookings/leads", title: "Leads" },
            {
              link: "/bookings/lead/booking-details",
              title: "Booking Details",
              on: true,
            },
          ]}
        />
      </Box>

      <Text fontSize="24px" fontWeight="600" mb={6}>
        Lead/Add Lead/<span style={{ color: "#2563eb" }}>Booking Details</span>
      </Text>

      {bookingData?.gstOnCommission !== undefined && (
        <Box borderWidth="1px" borderRadius="lg" p={6} mb={6} bg="white">
          <Text fontSize="18px" fontWeight="600" mb={2}>
            Booking Summary
          </Text>
          <Text fontSize="md" color="gray.700">
            <strong>GST on Commission:</strong> {bookingData.gstOnCommission}
          </Text>
        </Box>
      )}

      {/* CUSTOMER DETAILS */}
      <Box borderWidth="1px" borderRadius="lg" p={6} mb={6} bg="white">
        <Text fontSize="18px" fontWeight="600" mb={4}>
          Customer Details
        </Text>

        <Grid templateColumns="repeat(4,1fr)" gap={4}>
          <FormControl isInvalid={errors.name}>
            <FormLabel>Name</FormLabel>
            <Input
              name="name"
              value={form.name}
              onChange={handleChange}
              placeholder="Enter Name"
              maxLength={50}
            />
            {errors.name && <FormErrorMessage>{errors.name}</FormErrorMessage>}
          </FormControl>

          <FormControl isInvalid={errors.email}>
            <FormLabel>Email ID</FormLabel>
            <Input
              name="email"
              value={form.email}
              onChange={handleChange}
              placeholder="Enter Email"
              type="email"
              maxLength={100}
            />
            {errors.email && (
              <FormErrorMessage>{errors.email}</FormErrorMessage>
            )}
          </FormControl>

          <FormControl isInvalid={errors.mobile}>
            <FormLabel>Mobile Number</FormLabel>
            <Input
              name="mobile"
              value={form.mobile}
              onChange={handleChange}
              placeholder="Enter Mobile Number"
              inputMode="numeric"
              maxLength={10}
            />
            {errors.mobile && (
              <FormErrorMessage>{errors.mobile}</FormErrorMessage>
            )}
          </FormControl>

          <FormControl isInvalid={errors.whatsapp}>
            <FormLabel>Whatsapp Number</FormLabel>
            <Input
              name="whatsapp"
              value={form.whatsapp}
              onChange={handleChange}
              placeholder="Enter Whatsapp Number"
              isDisabled={whatsappSameAsMobile}
              inputMode="numeric"
              maxLength={10}
            />
            <Checkbox
              mt={2}
              size="sm"
              isChecked={whatsappSameAsMobile}
              onChange={(e) => {
                setWhatsappSameAsMobile(e.target.checked);
                if (e.target.checked) {
                  setForm((prev) => ({ ...prev, whatsapp: prev.mobile }));
                }
              }}
            >
              Same as mobile number
            </Checkbox>
            {errors.whatsapp && (
              <FormErrorMessage>{errors.whatsapp}</FormErrorMessage>
            )}
          </FormControl>

          <FormControl isInvalid={errors.city}>
            <FormLabel>City</FormLabel>
            <Input
              name="city"
              value={form.city}
              onChange={handleChange}
              placeholder="Enter City"
              maxLength={50}
            />
            {errors.city && <FormErrorMessage>{errors.city}</FormErrorMessage>}
          </FormControl>

          <FormControl isInvalid={errors.gender}>
            <FormLabel>Gender</FormLabel>
            <Select
              name="gender"
              value={form.gender}
              onChange={handleChange}
              placeholder="Select Gender"
            >
              <option>Male</option>
              <option>Female</option>
              <option>Other</option>
            </Select>
            {errors.gender && (
              <FormErrorMessage>{errors.gender}</FormErrorMessage>
            )}
          </FormControl>

          <Box>
            <FormLabel>Language</FormLabel>
            <Input
              name="language"
              value={form.language}
              onChange={handleChange}
              placeholder="English, Hindi"
              maxLength={60}
            />
            {errors.language && (
              <Text color="red.500" fontSize="sm" mt={1}>
                {errors.language}
              </Text>
            )}
          </Box>

          <Box>
            <FormLabel>Comments</FormLabel>
            <Input
              name="comments"
              value={form.comments}
              onChange={handleChange}
              placeholder="Enter Notes"
              maxLength={250}
            />
          </Box>
        </Grid>

        <Checkbox mt={4}>Corporate Client</Checkbox>
      </Box>

      {/* PASSENGER DETAILS */}
      <Box borderWidth="1px" borderRadius="lg" p={6} bg="white">
        <Flex justify="space-between" align="center" mb={4}>
          <Text fontSize="18px" fontWeight="600">
            Passenger Details
          </Text>
          <Button
            leftIcon={<FiPlus />}
            colorScheme="blue"
            variant="outline"
            size="sm"
            onClick={addPassenger}
          >
            Add Passenger
          </Button>
        </Flex>

        <Checkbox
          mb={4}
          defaultChecked
          onChange={(e) => copyCustomerToFirstPassenger(e.target.checked)}
        >
          Same as booked customer
        </Checkbox>

        {passengers.map((passenger, index) => (
          <Box
            key={index}
            mb={4}
            p={4}
            borderWidth="1px"
            borderRadius="md"
            bg="gray.50"
            position="relative"
          >
            <HStack justify="space-between" mb={3}>
              <Text fontWeight="600" color="gray.700">
                Passenger {index + 1}
              </Text>
              {passengers.length > 1 && (
                <IconButton
                  icon={<FiTrash2 />}
                  colorScheme="red"
                  variant="ghost"
                  size="sm"
                  onClick={() => removePassenger(index)}
                  aria-label="Remove passenger"
                />
              )}
            </HStack>

            <Grid templateColumns="repeat(3,1fr)" gap={4}>
              <FormControl isInvalid={passengerErrors[index]?.name}>
                <FormLabel>Name</FormLabel>
                <Input
                  value={passenger.name}
                  onChange={(e) =>
                    handlePassengerChange(index, "name", e.target.value)
                  }
                  placeholder="Passenger Name"
                  bg="white"
                  maxLength={50}
                />
                {passengerErrors[index]?.name && (
                  <FormErrorMessage>
                    {passengerErrors[index].name}
                  </FormErrorMessage>
                )}
              </FormControl>

              <FormControl isInvalid={passengerErrors[index]?.mobile}>
                <FormLabel>Mobile Number</FormLabel>
                <Input
                  value={passenger.mobile}
                  onChange={(e) =>
                    handlePassengerChange(index, "mobile", e.target.value)
                  }
                  placeholder="Enter Mobile Number"
                  bg="white"
                  inputMode="numeric"
                  maxLength={10}
                />
                {passengerErrors[index]?.mobile && (
                  <FormErrorMessage>
                    {passengerErrors[index].mobile}
                  </FormErrorMessage>
                )}
              </FormControl>

              <FormControl isInvalid={passengerErrors[index]?.whatsapp}>
                <FormLabel>Whatsapp Number</FormLabel>
                <Input
                  value={passenger.whatsapp}
                  onChange={(e) =>
                    handlePassengerChange(index, "whatsapp", e.target.value)
                  }
                  placeholder="Enter Whatsapp Number"
                  bg="white"
                  isDisabled={passengerWhatsappSameAsMobile[index]}
                  inputMode="numeric"
                  maxLength={10}
                />
                <Checkbox
                  mt={2}
                  size="sm"
                  isChecked={passengerWhatsappSameAsMobile[index]}
                  onChange={(e) => {
                    const newStates = [...passengerWhatsappSameAsMobile];
                    newStates[index] = e.target.checked;
                    setPassengerWhatsappSameAsMobile(newStates);
                    if (e.target.checked) {
                      handlePassengerChange(
                        index,
                        "whatsapp",
                        passenger.mobile,
                      );
                    }
                  }}
                >
                  Same as mobile number
                </Checkbox>
                {passengerErrors[index]?.whatsapp && (
                  <FormErrorMessage>
                    {passengerErrors[index].whatsapp}
                  </FormErrorMessage>
                )}
              </FormControl>
            </Grid>
          </Box>
        ))}
      </Box>

      {/* FOOTER */}
      <Flex justify="flex-end" mt={6} gap={4}>
        <Button variant="outline" onClick={() => navigate(-1)}>
          Cancel
        </Button>
        <Button colorScheme="blue" onClick={handleSubmit}>
          Add Booking
        </Button>
      </Flex>
    </Box>
  );
};

export default Booking;
